import React, { useEffect, useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Drawer, DrawerContent } from '@yuhuinc/ui-library';
import { Space, Form, Input, Button, Radio, Divider, Select, message } from 'antd';
import styled from 'styled-components';
import { getAgents } from 'actions/landlordPropertiesActions';
import { CurrentApplicationContext } from '../../ApplicationOverview';
import { getTranslatedString } from 'helpers/i18n';
import { apiPost } from 'helpers/ApiHelper';
import { useQueryClient } from '@tanstack/react-query';

const StyledDivider = styled(Divider)`
  margin-top: 8px;
  margin-bottom: 4px;
`;

const StyledSpace = styled(Space)`
  width: 100%;

  .ant-picker {
    width: 100%;
  }
`;

const translate = (key) =>
  getTranslatedString(
    `landlordPortal.applicationManagement.applicationOverview.review.reviewDrawer.${key}`
  );

const ReviewDrawer = ({ isVisible, onClose, getAgents, agents }) => {
  const application = useContext(CurrentApplicationContext);
  const [form] = Form.useForm();
  const recommendation = Form.useWatch('recommendation', form);
  const queryClient = useQueryClient();

  const createApplicationReviewMutation = useMutation({
    mutationFn: (data) => {
      return apiPost(
        `/landlords/application_management/applications/${application.id}/review`,
        data
      );
    },
    onSuccess() {
      message.success(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.review.submitted.success'
        )
      );
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError() {
      // TODO: Ask Product for a proper error message
      message.error(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.review.submitted.error'
        )
      );
    },
  });

  const onCloseDrawer = () => {
    form.resetFields();
    onClose();
  };

  const onSubmitReview = (values) => {
    createApplicationReviewMutation.mutate(values);
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (['approve', 'deny'].includes(recommendation)) {
      getAgents({
        can_approve: application.id,
      });
    } else if (recommendation === 'additional_review') {
      getAgents({
        can_review: application.id,
      });
    }
  }, [recommendation]);

  return (
    <Drawer title={'Review'} visible={isVisible} closable destroyOnClose onClose={onCloseDrawer}>
      <DrawerContent>
        <Form layout="vertical" form={form} onFinish={onSubmitReview} scrollToFirstError>
          <StyledSpace direction="vertical" size="middle">
            <Form.Item
              label={translate('recommendationLabel')}
              name="recommendation"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value="approve">{translate('recommendApprove')}</Radio>
                  <Radio value="deny">{translate('recommendDeny')}</Radio>
                  <Radio value="additional_review">{translate('recommendAdditionalReview')}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            <StyledDivider />

            {recommendation ? (
              <>
                <Form.Item
                  label={translate(
                    recommendation === 'additional_review' ? 'reviewer' : 'approver'
                  )}
                  name="agent_to_notify_id"
                  rules={[{ required: true }]}
                >
                  <Select placeholder={translate('selectReviewer')}>
                    {agents.map((agent) => (
                      <Select.Option value={agent.value} key={agent.value}>
                        {agent.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label={translate('notes')}
                  name="notes"
                  extra={translate('notesExtraInfo')}
                >
                  <Input.TextArea rows={4} />
                </Form.Item>
              </>
            ) : null}

            <Form.Item shouldUpdate>
              {() => (
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={
                    !form.isFieldsTouched(['recommendation', 'agent_to_notify_id'], true) ||
                    form.getFieldsError().filter(({ errors }) => errors.length).length > 0
                  }
                >
                  {getTranslatedString('submit')}
                </Button>
              )}
            </Form.Item>
          </StyledSpace>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

ReviewDrawer.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
};

ReviewDrawer.defaultProps = {
  isVisible: false,
  onClose: () => {},
};

const mapStoreToProps = (store) => ({
  agents: store.landlordProperties.agents,
});

const mapDispatchToProps = {
  getAgents,
};

export default connect(mapStoreToProps, mapDispatchToProps)(ReviewDrawer);
